import React from "react";
import { Customer } from "../../services/customers";

interface CustomerRowProps {
  customer: Customer;
  updatingStatus: string | null;
  editingStatus: string | null;
  deletingCustomer: string | null;
  showDeleteConfirm: string | null;
  onStatusUpdate: (customerId: string, newStatus: string) => Promise<void>;
  onToggleStatusEdit: (customerId: string) => void;
  onDeleteClick: (customerId: string) => void;
  onDeleteConfirm: (customerId: string) => Promise<void>;
  onDeleteCancel: () => void;
  formatDate: (dateString: string) => string;
  formatPhoneNumber: (countryCode: string, phoneNumber: string) => string;
  getStatusColor: (status: string) => string;
}

export const CustomerRow: React.FC<CustomerRowProps> = ({
  customer,
  updatingStatus,
  editingStatus,
  deletingCustomer,
  showDeleteConfirm,
  onStatusUpdate,
  onToggleStatusEdit,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
  formatDate,
  formatPhoneNumber,
  getStatusColor,
}) => {
  const isUpdating = updatingStatus === customer.id;
  const isEditing = editingStatus === customer.id;
  const isDeleting = deletingCustomer === customer.id;
  const showConfirm = showDeleteConfirm === customer.id;

  return (
    <tr className="hover:bg-gray-50 transition-colors duration-150">
      {/* Customer Info */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
              <span className="text-white font-medium text-sm">
                {customer.full_name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {customer.full_name}
            </div>
            <div className="text-sm text-gray-500 font-mono">
              ID: {customer.id.slice(0, 8)}...
            </div>
          </div>
        </div>
      </td>

      {/* Contact Info */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 font-medium">
          {customer.email}
        </div>
        <div className="text-sm text-gray-500">
          {customer.phone_country_code && customer.phone_number
            ? formatPhoneNumber(
                customer.phone_country_code,
                customer.phone_number
              )
            : "No phone provided"}
        </div>
      </td>

      {/* Location Info */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 font-medium">
          {customer.country_of_residence || "Not specified"}
        </div>
        <div className="text-sm text-gray-500">
          Citizen: {customer.country_of_citizenship || "Not specified"}
        </div>
        <div className="text-xs text-gray-400">
          Status: {customer.canadian_status || "Not specified"}
        </div>
      </td>

      {/* Service Info */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 capitalize">
          {customer.service_reason
            ? customer.service_reason.replace("_", " ")
            : "Not specified"}
        </div>
        <div className="text-xs text-gray-500">
          Terms:{" "}
          <span
            className={`font-medium ${
              customer.agree_to_terms ? "text-green-600" : "text-red-600"
            }`}
          >
            {customer.agree_to_terms ? "✓ Agreed" : "✗ Not agreed"}
          </span>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        {isUpdating ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-xs text-gray-500">Updating...</span>
          </div>
        ) : isEditing ? (
          <div className="flex items-center space-x-2">
            <select
              value={customer.status}
              onChange={(e) => onStatusUpdate(customer.id, e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-150"
              autoFocus
            >
              <option value="Unprocessed">Unprocessed</option>
              <option value="Processed">Processed</option>
            </select>
            <button
              onClick={() => onToggleStatusEdit(customer.id)}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-150"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            onClick={() => onToggleStatusEdit(customer.id)}
            className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border cursor-pointer hover:shadow-sm transition-all duration-200 ${getStatusColor(
              customer.status
            )}`}
            title="Click to change status"
          >
            {customer.status}
            <span className="ml-1 text-xs opacity-60">▼</span>
          </button>
        )}
      </td>

      {/* Dates */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-xs text-gray-500 space-y-1">
          <div>
            <span className="font-medium">Created:</span>{" "}
            {formatDate(customer.created_at)}
          </div>
          <div>
            <span className="font-medium">Updated:</span>{" "}
            {formatDate(customer.updated_at)}
          </div>
        </div>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap">
        {showConfirm ? (
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 font-medium">Delete?</span>
            <button
              onClick={() => onDeleteConfirm(customer.id)}
              disabled={isDeleting}
              className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              {isDeleting ? "..." : "Yes"}
            </button>
            <button
              onClick={onDeleteCancel}
              disabled={isDeleting}
              className="text-xs px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              No
            </button>
          </div>
        ) : (
          <button
            onClick={() => onDeleteClick(customer.id)}
            disabled={isDeleting}
            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm"
            title="Delete customer"
          >
            {isDeleting ? (
              <div className="flex items-center space-x-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                <span>Deleting...</span>
              </div>
            ) : (
              "Delete"
            )}
          </button>
        )}
      </td>
    </tr>
  );
};
